import type { Consumer } from "@rails/actioncable";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createCable } from "./cable";
import { useAuth } from "./contexts/AuthProvider";
import { setWsCookie } from "./helpers/setWsCookie";

type CableContextValue = {
  cable: Consumer;
  // True once the WS auth cookie is set and the cable is connected for
  // the current user — subscriptions must wait for it (see the prepare
  // effect below).
  ready: boolean;
};

const CableContext = createContext<CableContextValue | null>(null);

// Min away duration before we force a reconnect on return. Short
// blurs / tab flicks don't need a reconnect; long absences (Chrome may
// have suspended the tab and silently dropped the WebSocket) do.
const RECONNECT_AFTER_AWAY_MS = 5000;

// Cadence for the periodic connection health check. Cheap (just reads
// the WebSocket readyState) so we can run it often.
const HEALTH_CHECK_INTERVAL_MS = 5000;

// ActionCable internals — not in the public type, but stable across the
// major versions we've used. `monitor.connectionIsStale()` compares the
// last received server ping (or the connection start time if no ping yet)
// against the 6s staleThreshold (2× the 3s server beat interval). When
// stale, the underlying socket is presumed dead even if readyState still
// reports OPEN — that's the case WebSocket "idle timeout" bugs fall into.
type CableInternals = Consumer & {
  connection: {
    isOpen: () => boolean;
    isActive: () => boolean;
    monitor: {
      connectionIsStale: () => boolean;
      pingedAt?: number;
    };
  };
  ensureActiveConnection: () => void;
};

export function CableProvider({ children }: { children: ReactNode }) {
  const cable = useMemo<Consumer>(() => createCable(), []);
  const { token } = useAuth();
  const tokenRef = useRef(token);
  const awayAtRef = useRef<number | null>(null);
  // Guard against overlapping recoveries — focus + visible can fire
  // back-to-back when the user alt-tabs back to a hidden tab.
  const recoveringRef = useRef(false);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Prepare the cable for the current user: the connection's identity is
  // fixed at handshake time from a one-shot cookie, so the cookie must be
  // set before connecting — and re-set on a user change (after a
  // logout → login in the same tab the singleton consumer would otherwise
  // keep streaming for the previous user until a page reload). `ready`
  // gates subscriptions so the first connect never races the cookie fetch.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!token) {
        cable.disconnect();

        return;
      }

      try {
        await setWsCookie(token);
      } catch (err) {
        // Non-fatal: connect anyway — the retry / recover() paths get
        // another chance later.
        // eslint-disable-next-line no-console
        console.warn("WS cookie set failed before connect", err);
      }

      if (cancelled) return;

      // Cycle a connection that may still belong to the previous user.
      cable.disconnect();
      cable.connect();
      setReady(true);
    })();

    return () => {
      cancelled = true;
      setReady(false);
      // The singleton consumer outlives this provider — close the socket
      // on unmount (logout) so the previous user's connection never lingers.
      cable.disconnect();
    };
  }, [cable, token]);

  // Recover stale WebSocket connections after the user has been away
  // from the tab. We track "away" via two signals:
  //
  //   - `visibilitychange` (hidden) — fires when the tab is backgrounded
  //     or the window minimised.
  //   - `window.blur` — fires when the OS focus moves to another app
  //     (e.g. Chrome → RubyMine on macOS). visibilitychange does NOT
  //     fire in this case because the Chrome window is still on-screen,
  //     just unfocused — so without a blur listener, app-switching on
  //     macOS would never trigger recovery.
  //
  // On return, if the away duration crossed the threshold we call
  // recover(). recover() does a liveness check first: if the server
  // pinged us within the last 6 seconds (ActionCable's built-in
  // staleThreshold), the socket is demonstrably alive and we leave it
  // alone. Brief alt-tabs don't interrupt the ping stream, so this
  // skips the reconnect for the common case.
  //
  // If the connection IS stale, we do the full recovery:
  //   1. Refresh the WS auth cookie. The cable cookie is short-lived,
  //      and reconnecting with a stale cookie causes the server to
  //      reject the WebSocket handshake — at which point ActionCable's
  //      ConnectionMonitor retries forever with the same stale cookie
  //      and eventually gives up. We MUST refresh before reopening.
  //   2. Hard-cycle the connection: an idle WebSocket can have
  //      `readyState === OPEN` while the underlying TCP is dead, so we
  //      don't trust `connection.isOpen()` here — the ping-based
  //      staleness check above is what we rely on.
  //
  // The previous version called `cable.disconnect()` + `cable.connect()`
  // without a cookie refresh, which silently failed in exactly this way:
  // the new socket connects with a stale cookie, the server rejects it,
  // the monitor loops on the same cookie, and the green icon never
  // comes back.
  useEffect(() => {
    const consumer = cable as CableInternals;

    const markAway = () => {
      // Latch the earliest away time so e.g. blur followed by hidden
      // doesn't reset the clock.
      if (awayAtRef.current == null) {
        awayAtRef.current = Date.now();
      }
    };

    const recover = async () => {
      if (recoveringRef.current) return;
      // If the server has pinged us within the last 6s, the socket is
      // demonstrably alive — skip the destructive disconnect/connect
      // dance. This is the common case when the user briefly switches
      // to another app and back: Chrome keeps the socket awake, pings
      // keep arriving, and there's nothing to recover. Only when pings
      // stop arriving (Chrome has suspended the tab, or the network
      // actually dropped) do we force the reconnect + cookie refresh.
      try {
        if (!consumer.connection.monitor.connectionIsStale()) return;
      } catch {
        // If the internal shape ever changes, fall through to the old
        // behavior rather than leaving the user stuck on a dead socket.
      }
      recoveringRef.current = true;
      try {
        const t = tokenRef.current;
        if (t) {
          try {
            await setWsCookie(t);
          } catch (err) {
            // Cookie refresh failure is non-fatal — try the reconnect
            // anyway. If the cookie is still valid the new socket will
            // connect; if not, the next focus return will retry.
            // eslint-disable-next-line no-console
            console.warn("WS cookie refresh failed, reconnecting anyway", err);
          }
        }
        cable.disconnect();
        cable.connect();
      } finally {
        recoveringRef.current = false;
      }
    };

    const checkRecover = () => {
      const awayAt = awayAtRef.current;
      awayAtRef.current = null;
      if (awayAt == null) return;

      const awayMs = Date.now() - awayAt;
      if (awayMs < RECONNECT_AFTER_AWAY_MS) return;

      void recover();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        markAway();
      } else if (document.visibilityState === "visible") {
        checkRecover();
      }
    };

    // Periodic safety net for the case where the network blips while
    // the tab stays focused (no visibility/focus event fires). Cheap:
    // when the socket is genuinely open this is a no-op. When it isn't,
    // we run the same cookie-refresh + reconnect path so we don't get
    // stuck reconnecting with a stale cookie.
    const periodicCheck = () => {
      if (document.visibilityState !== "visible") return;
      if (consumer.connection.isOpen()) return;
      void recover();
    };

    const onOnline = () => void recover();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", markAway);
    window.addEventListener("focus", checkRecover);
    window.addEventListener("online", onOnline);
    const interval = setInterval(periodicCheck, HEALTH_CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", markAway);
      window.removeEventListener("focus", checkRecover);
      window.removeEventListener("online", onOnline);
      clearInterval(interval);
    };
  }, [cable]);

  const value = useMemo<CableContextValue>(
    () => ({ cable, ready }),
    [cable, ready],
  );

  return (
    <CableContext.Provider value={value}>{children}</CableContext.Provider>
  );
}

function useCableContext(): CableContextValue {
  const ctx = useContext(CableContext);

  if (!ctx) {
    throw new Error("useCable must be used within a <CableProvider>");
  }

  return ctx;
}

export function useCable(): Consumer {
  return useCableContext().cable;
}

export function useCableReady(): boolean {
  return useCableContext().ready;
}
